<template>
  <div class="labeled-control">
    <p :class="{ wm_label_disabled: disabled }" v-if="label">
      {{ label }}
    </p>
    <input
      class="wm_numeric_input"
      :placeholder="placeholder"
      :value="inputValue"
      @input="onInput"
      @blur="onBlur"
      :disabled="disabled"
      :class="{
        wm_input_disabled: disabled,
        wm_out_of_range: outOfRange,
        wm_input_limit_reached: limitReached,
        wm_input_tightness1: tightness == 1,
        wm_input_tightness2: tightness == 2,
      }"
      type="number"
      :step="step"
      :min="min"
      :max="max"
      :title="hint"
    />
  </div>
</template>

<script>
export default {
  name: "NumericInput",
  props: {
    label: {
      type: String
    },
    placeholder: {
      type: String,
      default: "Enter value"
    },
    modelValue: {},
    disabled: {
      type: Boolean
    },
    tightness: {
      default: 0
    },
    step: {
      default: 1
    },
    min: {
      default: -100000
    },
    max: {
      default: 100000
    },
    hintText: {
      type: String,
      default: ""
    },
    limitReached: {
      type: Boolean,
      default: false
    },
    limitReachedHint: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      inputValue: this.modelValue == null ? "" : String(this.modelValue),
    };
  },
  watch: {
    modelValue(v) {
      // Do not stomp a user-typed "-0" with "0"
      if (v === 0 && typeof this.inputValue === "string" && this.inputValue.startsWith("-0")) {
        return;
      }

      const next = v == null ? "" : String(v);
      if (next !== this.inputValue) {
        this.inputValue = next;
      }
    },
  },
  computed: {
    outOfRange() {
      const num = parseFloat(this.inputValue);
      return !isNaN(num) && (num < this.min || num > this.max);
    },
    hint() {
      const base = this.hintText
        ? `${this.hintText} (${this.min}…${this.max})`
        : `${this.min}…${this.max}`;
      if (this.limitReached && this.limitReachedHint) {
        return `${this.limitReachedHint}\n${base}`;
      }
      return base;
    },
  },
  methods: {
    onInput(e) {
      const value = e.target.value;
      this.inputValue = value;

      // Allow "in-progress" values without immediately resetting:
      // "-", ".", "-." are potentially valid prefixes, don't emit yet.
      if (value === "-" || value === "." || value === "-.") {
        return;
      }

      // Empty: clear the model
      if (value === "") {
        this.$emit("update:modelValue", null);
        return;
      }

      const num = parseFloat(value);
      if (isNaN(num)) {
        // Invalid numeric string: keep what the user sees,
        // but do not change the modelValue.
        return;
      }

      if (num < this.min || num > this.max) {
        return;
      }
      this.$emit("update:modelValue", num);
    },
    onBlur(e) {
      const num = parseFloat(this.inputValue);
      if (isNaN(num)) {
        return;
      }

      const clamped = Math.min(this.max, Math.max(this.min, num));
      if (clamped !== num) {
        this.inputValue = String(clamped);
        e.target.value = String(clamped);
        this.$emit("update:modelValue", clamped);
      }
    },
  },
};
</script>
